import { FileJson2, ListChecks } from "lucide-react";

import { usePromptInitialInputs } from "./hooks/usePromptInitialInputs";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PromptVersion } from "@/types/apiGetters";

interface CustomPresetSetupProps {
  promptVersion?: PromptVersion;
  isSubmitting: boolean;
  onSubmit: (initialInputs: Record<string, string>) => void;
}

const CustomPresetSetup = ({ promptVersion, isSubmitting, onSubmit }: CustomPresetSetupProps) => {
  const { inputVariables, initialInputs, setInitialInputs } = usePromptInitialInputs(promptVersion);

  return (
    <div className="flex min-h-0 flex-col">
      <div className="max-h-[58vh] overflow-y-auto px-4 py-4 sm:px-5">
        <div className="border-b border-gray-100 pb-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-gray-950">First row</p>
            <span className="text-xs text-gray-400">Optional</span>
          </div>

          {inputVariables.length > 0 ? (
            <div className="space-y-1.5">
              {inputVariables.map(variable => (
                <div
                  key={variable}
                  className="grid gap-1.5 sm:grid-cols-[120px_minmax(0,1fr)] sm:items-center sm:gap-2"
                >
                  <label
                    className="truncate text-xs text-gray-500"
                    htmlFor={`custom-preset-input-${variable}`}
                  >
                    {variable}
                  </label>
                  <Input
                    id={`custom-preset-input-${variable}`}
                    className="h-8 border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
                    value={initialInputs[variable] ?? ""}
                    onChange={event =>
                      setInitialInputs(previous => ({
                        ...previous,
                        [variable]: event.target.value,
                      }))
                    }
                    placeholder="Value"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-200 bg-white p-3 text-sm leading-5 text-gray-500">
              This prompt version does not have input variables. The table will start with a prompt
              output column.
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            Table this creates
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-lg bg-gray-50 p-2.5">
              <ListChecks className="mb-2 h-4 w-4 text-gray-500" />
              <p className="text-xs font-medium text-gray-900">Inputs</p>
              <p className="mt-0.5 text-xs leading-4 text-gray-500">
                {inputVariables.length > 0
                  ? "One editable text column per prompt variable."
                  : "No input columns for this prompt version."}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-2.5">
              <FileJson2 className="mb-2 h-4 w-4 text-gray-500" />
              <p className="text-xs font-medium text-gray-900">Output</p>
              <p className="mt-0.5 text-xs leading-4 text-gray-500">
                A prompt template column connected to the input variables.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="text-xs text-gray-500">
            {inputVariables.length > 0
              ? `${inputVariables.length} input variable${inputVariables.length === 1 ? "" : "s"}`
              : "Ready to create."}
          </p>
          <Button type="button" onClick={() => onSubmit(initialInputs)} disabled={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create and run"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomPresetSetup;
